import * as config from '../config';
import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import ModelLoader from '../ModelLoader';
import logging from '../logging';
import { BufferGeometry, Material, Mesh, Object3D } from 'three';
import { Lights } from '../domain';
import { zip } from '../utils';

const log = logging('view');

const ml = ModelLoader.getInstance();

export default class LightsView extends BaseView {

    declare model: Lights;
    mesh: Mesh;

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);
        this.init();
    }

    make() {
        this.mesh = new Mesh(new BufferGeometry(), [new Material()]);
        this.mesh.castShadow = this.mesh.receiveShadow = true;
    }

    bind() {
        this.updateGeometry = this.updateGeometry.bind(this);
        this.updateLayout = this.updateLayout.bind(this);

        this.model.bind('materials', () => {
            this.bindMaterialArray(this.mesh, this.model.materials);
        });

        this.model.bind('type', this.updateGeometry);
        this.model.on('count', this.updateGeometry);

        this.model.on('width', this.updateLayout);
        this.model.on('height', this.updateLayout);
        this.model.on('depth', this.updateLayout);
    }

    updateGeometry() {
        for(const mesh of Array.from(this.children)) {
            mesh.removeFromParent();
        }
        if(this.model.type == 'strip') {
            this.updates.queue(
                () => ml.get(`${config.models}/strip_light_horizontal.obj`),
                (obj: Object3D) => {
                    obj.traverse(child => {
                        if(child instanceof Mesh) {
                            this.mesh.geometry = child.geometry;
                        }
                    });
                    this.mesh.geometry.addGroup(
                        0, this.mesh.geometry.attributes.position.count, 0);
                    this.add(this.mesh);
                    this.updateLayout();
                    this.pipelineUpdate();
                });
        }
        // otherwise this is a puck light or set of puck lights
        else {
            this.updates.queue(
                () => ml.get(`${config.models}/puck_light.obj`),
                (obj: Object3D) => {
                    obj.traverse(child => {
                        if(child instanceof Mesh){
                            this.mesh.geometry = child.geometry;
                        }
                    });

                    for(let i=0; i < this.model.count; i++) {
                        this.add(this.mesh.clone());
                    }
                    this.updateLayout();
                    this.pipelineUpdate();
                });
        }
    }

    updateLayout() {
        if(this.children.length == 0) {
            return; // nothing to do until we have updated geometry
        }
        if(this.model.type == 'strip') {
            const obj = this.children[0];
            obj.scale.x = this.model.width;
            obj.position.x = this.model.width / 2;
            obj.position.y = this.model.height;
            obj.position.z = this.model.depth;
        }
        else {
            let positions: number[];

            switch(this.model.count) {
                case 1:
                    positions = [0.5];
                    break;
                case 2:
                    positions = [0.25, 0.75];
                    break;
                case 3:
                    positions = [0.25, 0.5, 0.75];
                    break;
                case 4:
                    positions = [0.2, 0.4, 0.6, 0.8];
                    break;
                default:
                    positions = [];
                    log.error(`LightsView.updateLayout() unsupported puck light count ${this.model.count}`);
            }

            for(const [position, puck] of zip(positions, this.children)) {
                puck.position.x = position * this.model.width;
                puck.position.y = this.model.height;
                puck.position.z = this.model.depth;
            }

        }
    }
}
